import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { User, ArrowLeft, Save, Lock, Upload, Trash2, Trophy, Gamepad2, Share2 } from 'lucide-react';
import axios from 'axios';
import Avatar from './Avatar';
import CountrySelector from './CountrySelector';
import ImageCropModal from './ImageCropModal';
import FullImageModal from './FullImageModal';
import LanguageSwitcher from "./LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import PlaceMedal from './PlaceMedal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Share buttons component
const ShareButtons = ({ url, text, compact = false, whiteShadow = false, shareToTelegram, shareToWhatsApp, shareToVK, shareToOK, copyToClipboard }) => {
  const { t, language } = useLanguage();
  const iconSize = compact ? 22 : 20;
  const shadowStyle = whiteShadow ? '0 2px 8px rgba(255,255,255,0.8), 0 1px 3px rgba(0,0,0,0.3)' : 'none';
  
  const buttonStyle = compact ? {
    padding: '0.4rem',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s, opacity 0.15s',
    boxShadow: shadowStyle
  } : {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.6rem 1rem',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 500
  };

  const handleHover = (e, isEnter) => {
    if (compact) {
      e.currentTarget.style.transform = isEnter ? 'scale(1.15)' : 'scale(1)';
      e.currentTarget.style.opacity = isEnter ? '0.9' : '1';
    }
  };

  return (
    <div style={{ display: 'flex', gap: compact ? '0.4rem' : '0.75rem', flexWrap: 'wrap' }}>
      <button
        onClick={(e) => { e.stopPropagation(); shareToTelegram(url, text); }}
        onMouseEnter={(e) => handleHover(e, true)}
        onMouseLeave={(e) => handleHover(e, false)}
        style={{ ...buttonStyle, background: '#0088cc', color: 'white' }}
        title="Telegram"
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
        {!compact && 'Telegram'}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); shareToWhatsApp(url, text); }}
        onMouseEnter={(e) => handleHover(e, true)}
        onMouseLeave={(e) => handleHover(e, false)}
        style={{ ...buttonStyle, background: '#25D366', color: 'white' }}
        title="WhatsApp"
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        {!compact && 'WhatsApp'}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); shareToVK(url, text); }}
        onMouseEnter={(e) => handleHover(e, true)}
        onMouseLeave={(e) => handleHover(e, false)}
        style={{ ...buttonStyle, background: '#0077FF', color: 'white' }}
        title="VK"
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4 8.49 4 8.08c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.847 2.455 2.27 4.607 2.86 4.607.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.372 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.253-1.406 2.15-3.574 2.15-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
        </svg>
        {!compact && 'VK'}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); shareToOK(url, text); }}
        onMouseEnter={(e) => handleHover(e, true)}
        onMouseLeave={(e) => handleHover(e, false)}
        style={{ ...buttonStyle, background: '#EE8208', color: 'white' }}
        title={t("share.ok")}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm0 4.8a4.2 4.2 0 1 1 0 8.4 4.2 4.2 0 0 1 0-8.4zm0 6.6a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8zm4.789 2.29a7.2 7.2 0 0 1-9.578 0 .9.9 0 0 0-1.211 1.33c.9.82 1.953 1.448 3.1 1.856l-2.648 2.647a.9.9 0 1 0 1.273 1.273L12 16.522l4.275 4.274a.9.9 0 0 0 1.273-1.273l-2.648-2.647a8.085 8.085 0 0 0 3.1-1.856.9.9 0 0 0-1.211-1.33z"/>
        </svg>
        {!compact && 'OK'}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); copyToClipboard(url, text); }}
        onMouseEnter={(e) => handleHover(e, true)}
        onMouseLeave={(e) => handleHover(e, false)}
        style={{ ...buttonStyle, background: 'linear-gradient(135deg, #7B68EE, #9370DB)', color: 'white' }}
        title={t('userProfileUI.maxCopyLink')}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" fill="white"/>
          <text x="12" y="16" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#7B68EE" fontFamily="Arial, sans-serif">M</text>
        </svg>
        {!compact && 'Max'}
      </button>
    </div>
  );
};

const UserProfile = ({ isPublic = false }) => {
  const { user: authUser, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { userId: paramUserId } = useParams();
  
  // Determine which user to show
  const [profileUser, setProfileUser] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(isPublic);
  const isOwnProfile = !isPublic || paramUserId === authUser?.id;
  const user = isOwnProfile ? authUser : profileUser;
  
  const [isEditing, setIsEditing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [deletingAvatar, setDeletingAvatar] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showAvatarGallery, setShowAvatarGallery] = useState(false);
  
  // Predefined avatar seeds for gallery
  const avatarGallery = [
    'explorer-1', 'explorer-2', 'explorer-3', 'explorer-4', 'explorer-5', 'explorer-6',
    'explorer-7', 'explorer-8', 'explorer-9', 'explorer-10', 'explorer-11', 'explorer-12',
    'traveler-1', 'traveler-2', 'traveler-3', 'traveler-4', 'traveler-5', 'traveler-6',
    'adventurer-1', 'adventurer-2', 'adventurer-3', 'adventurer-4', 'adventurer-5', 'adventurer-6',
    'player-1', 'player-2', 'player-3', 'player-4', 'player-5', 'player-6',
    'gamer-1', 'gamer-2', 'gamer-3', 'gamer-4', 'gamer-5', 'gamer-6',
    'hero-1', 'hero-2', 'hero-3', 'hero-4', 'hero-5', 'hero-6',
    'star-1', 'star-2', 'star-3', 'star-4', 'star-5', 'star-6',
    'champion-1', 'champion-2', 'champion-3', 'champion-4', 'champion-5', 'champion-6'
  ];
  
  const [formData, setFormData] = useState({
    email: authUser?.email || '',
    full_name: authUser?.full_name || '',
    password: '',
    confirmPassword: ''
  });
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);

  // Load public profile data if viewing another user
  useEffect(() => {
    const loadPublicProfile = async () => {
      if (!isPublic || !paramUserId) return;
      if (paramUserId === authUser?.id) {
        setProfileUser(authUser);
        setLoadingProfile(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/users/${paramUserId}/public`);
        setProfileUser(response.data);
      } catch (error) {
        console.error('Error loading public profile:', error);
        toast.error(t('toast.loadError'));
      } finally {
        setLoadingProfile(false);
      }
    };
    loadPublicProfile();
  }, [isPublic, paramUserId, authUser]);

  // Load user stats
  useEffect(() => {
    const loadStats = async () => {
      const targetUserId = isPublic ? paramUserId : authUser?.id;
      if (!targetUserId) return;
      try {
        const response = await axios.get(`${API}/users/${targetUserId}/stats`);
        setUserStats(response.data);
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    loadStats();
  }, [isPublic, paramUserId, authUser?.id]);

  // Load notifications (only for own profile)
  useEffect(() => {
    const loadNotifications = async () => {
      if (isPublic || !authUser) {
        setLoadingNotifications(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/notifications`, {
          headers: getAuthHeader()
        });
        setNotifications(response.data);
      } catch (error) {
        console.error('Error loading notifications:', error);
      } finally {
        setLoadingNotifications(false);
      }
    };
    loadNotifications();
  }, [isPublic, authUser]);

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: getAuthHeader()
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success(t('profile.notificationDeleted'));
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error(t('profile.errorDeletingNotification'));
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      for (const notification of notifications) {
        await axios.delete(`${API}/notifications/${notification.id}`, {
          headers: getAuthHeader()
        });
      }
      setNotifications([]);
      toast.success(t('profile.allNotificationsCleared'));
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error(t('profile.errorClearingNotifications'));
    }
  };

  // Share functions
  const getShareUrl = () => `https://countryscanner.ru/game`;
  const getShareText = () => t('share.inviteText');

  // Share game result
  const getGameResultUrl = (gameId) => `https://countryscanner.ru/game/games/${gameId}/user-results/${user?.id}`;
  const getGameShareText = (gameName, accuracy) => `Мой результат в игре "${gameName}": ${accuracy}% точности! Попробуй побить мой рекорд в Country Scanner!`;

  const shareToTelegram = (url, text) => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url || getShareUrl())}&text=${encodeURIComponent(text || getShareText())}`, '_blank');
  };

  const shareToWhatsApp = (url, text) => {
    const fullText = (text || getShareText()) + ' ' + (url || getShareUrl());
    window.open(`https://wa.me/?text=${encodeURIComponent(fullText)}`, '_blank');
  };

  const shareToVK = (url, text) => {
    window.open(`https://vk.com/share.php?url=${encodeURIComponent(url || getShareUrl())}&title=${encodeURIComponent(text || getShareText())}`, '_blank');
  };

  const shareToOK = (url, text) => {
    window.open(`https://connect.ok.ru/offer?url=${encodeURIComponent(url || getShareUrl())}&title=${encodeURIComponent(text || getShareText())}`, '_blank');
  };

  const copyToClipboard = async (url, text) => {
    const fullText = (text || getShareText()) + '\n' + (url || getShareUrl());
    try {
      await navigator.clipboard.writeText(fullText);
      toast.success(t('toast.linkCopied'));
    } catch (err) {
      toast.error(t('toast.copyFailed'));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('errors.selectImage'));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('toast.imageSizeError'));
      return;
    }

    // Read file as data URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result);
      setShowCropModal(true);
    };
    reader.onerror = (err) => {
      console.error('FileReader error:', err);
      toast.error(t('toast.loadError'));
    };
    reader.readAsDataURL(file);

    // Reset input
    e.target.value = '';
  };

  const handleCrop = async (croppedImage, fullImage, saveMode) => {
    
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('cropped_image', croppedImage);
      formData.append('save_full', saveMode === 'save-full' ? 'true' : 'false');
      if (fullImage) {
        formData.append('full_image', fullImage);
      }

      const response = await axios.post(`${API}/auth/me/avatar`, formData, {
        headers: {
          ...getAuthHeader(),
          
        }
      });

      toast.success(t('toast.avatarUpdated'));
      // Force reload with cache bust to show new avatar
      window.location.href = window.location.pathname + '?t=' + Date.now();
    } catch (error) {
      console.error('Avatar upload error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || t('toast.saveError'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarDelete = async () => {
    if (!window.confirm(t('confirm.deleteAvatar'))) return;

    setDeletingAvatar(true);
    try {
      await axios.delete(`${API}/auth/me/avatar`, {
        headers: getAuthHeader()
      });

      toast.success(t('toast.avatarDeleted'));
      window.location.reload();
    } catch (error) {
      console.error('Avatar delete error:', error);
      toast.error(error.response?.data?.detail || t('toast.deleteError'));
    } finally {
      setDeletingAvatar(false);
    }
  };

  const handleSelectPresetAvatar = async (seed) => {
    setUploadingAvatar(true);
    try {
      // Generate DiceBear URL with selected seed
      const avatarUrl = `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(seed)}&backgroundColor=667eea`;
      
      // Save selected avatar URL to user profile
      await axios.put(`${API}/auth/me`, {
        avatar_url: avatarUrl
      }, {
        headers: getAuthHeader()
      });

      toast.success(t('toast.avatarUpdated'));
      setShowAvatarGallery(false);
      window.location.reload();
    } catch (error) {
      console.error('Avatar selection error:', error);
      toast.error(error.response?.data?.detail || t('toast.saveError'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error(t('errors.passwordsMismatch'));
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error(t('errors.passwordMinLength'));
      return;
    }

    if (formData.full_name.trim().length < 3) {
      toast.error(t('errors.nameMinLength'));
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        email: formData.email,
        full_name: formData.full_name
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`${API}/auth/me`, updateData, {
        headers: getAuthHeader()
      });

      toast.success(t('toast.profileUpdated'));
      setIsEditing(false);
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
      
      // Reload user data
      window.location.reload();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || t('toast.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      email: user?.email || '',
      full_name: user?.full_name || '',
      password: '',
      confirmPassword: ''
    });
    setIsEditing(false);
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { headers: getAuthHeader() });
      toast.success(t('auth.emailSent'));
    } catch (error) {
      console.error('Error resending verification:', error);
      toast.error(error.response?.data?.detail || t('toast.sendError'));
    } finally {
      setResendingEmail(false);
    }
  };


  // Loading state for public profiles
  if (loadingProfile) {
    return (
      <div className="profile-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="animate-spin" style={{ width: 48, height: 48, border: '3px solid #e5e7eb', borderTopColor: '#667eea', borderRadius: '50%', margin: '0 auto 1rem' }} />
          <p style={{ color: '#6b7280' }}>{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  // User not found
  if (isPublic && !profileUser) {
    return (
      <div className="profile-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <User size={64} style={{ color: '#d1d5db', marginBottom: '1rem' }} />
          <h2 style={{ color: '#1f2937', marginBottom: '0.5rem' }}>{t('profile.userNotFound')}</h2>
          <Button variant="outline" onClick={() => navigate('/')}>{t('profile.toMain')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* New Header Layout */}
      <div className="profile-header-new">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="profile-back-button"
        >
          <ArrowLeft size={18} />
          <span className="back-text">{t('profile.back')}</span>
        </Button>
        <h1 className="profile-title">{isOwnProfile ? t('profile.myProfile') : user?.full_name || t('profile.playerProfile')}</h1>
      </div>

      <div className="profile-card" style={{ overflow: "visible" }}>
        {/* Public profile - simplified view */}
        {!isOwnProfile && (
          <div className="profile-info">
            <div className="profile-field" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              padding: '1.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              marginBottom: '1.5rem'
            }}>
              <Avatar
                userId={user?.id}
                avatarUrl={user?.avatar_url}
                fullImageUrl={user?.full_image_url}
                userName={user?.full_name}
                userEmail=""
                size={100}
                country={user?.country}
                onFullImageClick={(url) => setShowFullImage(url)}
              />
              <div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.25rem' }}>
                  {user?.full_name}
                </h2>
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  {t('profile.player')}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Own profile - full editing form */}
        {isOwnProfile && (
        <form onSubmit={handleSave}>
          <div className="profile-info">
            {/* Avatar Section */}
            <div className="profile-field" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              padding: '1.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <Avatar
                userId={user?.id}
                avatarUrl={user?.avatar_url}
                fullImageUrl={user?.full_image_url}
                userName={user?.full_name}
                userEmail={user?.email}
                size={80}
                country={user?.country}
                onFullImageClick={(url) => {
                  setShowFullImage(url);
                }}
              />
              <div style={{ flex: 1 }}>
                <label style={{ 
                  fontWeight: 600, 
                  marginBottom: '0.5rem',
                  display: 'block'
                }}>
                  {t('profile.avatar')}
                </label>
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: '#6c757d',
                  marginBottom: '0.75rem'
                }}>
                  {user?.avatar_url 
                    ? t('profile.yourAvatar') 
                    : t('profile.defaultAvatar')}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  <Button
                    type="button"
                    onClick={() => document.getElementById('avatar-upload').click()}
                    disabled={uploadingAvatar || deletingAvatar}
                    style={{
                      backgroundColor: '#667eea',
                      color: 'white',
                      fontSize: '0.875rem'
                    }}
                  >
                    <Upload size={16} style={{ marginRight: '0.5rem' }} />
                    {uploadingAvatar ? t('common.loading') : t('profile.uploadAvatar')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAvatarGallery(!showAvatarGallery)}
                    disabled={uploadingAvatar || deletingAvatar}
                    style={{
                      fontSize: '0.875rem'
                    }}
                  >
                    🎭 {showAvatarGallery ? t('profile.hideGallery') : t('profile.chooseFromGallery')}
                  </Button>
                  {user?.avatar_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAvatarDelete}
                      disabled={uploadingAvatar || deletingAvatar}
                      style={{
                        fontSize: '0.875rem',
                        color: '#dc2626'
                      }}
                    >
                      <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                      {deletingAvatar ? t('common.deleting') : t('common.delete')}
                    </Button>
                  )}
                </div>
                
                {/* Avatar Gallery */}
                {showAvatarGallery && (
                  <div style={{
                    padding: '1rem',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.75rem'
                    }}>
                      {t('profile.selectAvatar')}
                    </p>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                      gap: '0.5rem'
                    }}>
                      {avatarGallery.map((seed) => (
                        <div
                          key={seed}
                          onClick={() => handleSelectPresetAvatar(seed)}
                          style={{
                            cursor: 'pointer',
                            padding: '0.25rem',
                            borderRadius: '50%',
                            transition: 'all 0.2s',
                            border: '3px solid transparent',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#667eea';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'transparent';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <img
                            src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(seed)}&backgroundColor=667eea`}
                            alt="Avatar"
                            style={{
                              width: '50px',
                              height: '50px',
                              borderRadius: '50%'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {/* Crop Modal */}
            {showCropModal && selectedImage && (
              <ImageCropModal
                image={selectedImage}
                onClose={() => {
                  setShowCropModal(false);
                  setSelectedImage(null);
                }}
                onCrop={handleCrop}
              />
            )}

            {/* Full Image Modal */}
            {showFullImage && (
              <FullImageModal
                imageUrl={showFullImage}
                onClose={() => setShowFullImage(false)}
              />
            )}

            <div className="profile-field">
              <label>
                <User size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className="form-group input"
                  style={{ 
                    padding: '0.75rem', 
                    border: '2px solid #e2e8f0', 
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              ) : (
                <div className="profile-field-value">{user?.email}</div>
              )}
            </div>

            {/* Email Verification Status */}
            <div className="profile-field" style={{
              padding: '1rem',
              backgroundColor: user?.email_verified ? '#f0fdf4' : '#fef3c7',
              borderRadius: '8px',
              border: user?.email_verified ? '1px solid #86efac' : '1px solid #fde047'
            }}>
              <label style={{ marginBottom: '0.5rem', fontWeight: 600 }}>{t('profile.accountStatus')}</label>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    backgroundColor: user?.email_verified ? '#22c55e' : '#f59e0b',
                    color: 'white'
                  }}>
                    {user?.email_verified ? t('profile.active') : t('profile.notVerified')}
                  </span>
                  {!user?.email_verified && (
                    <span style={{ fontSize: '0.875rem', color: '#92400e' }}>
                      {t('profile.checkEmail')}
                    </span>
                  )}
                </div>
                {!user?.email_verified && (
                  <Button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendingEmail}
                    style={{
                      backgroundColor: '#667eea',
                      color: 'white',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem'
                    }}
                  >
                    {resendingEmail ? t('profile.sending') : t('profile.resendEmail')}
                  </Button>
                )}
              </div>
            </div>

            <div className="profile-field">
              <label>{t('profile.fullName')}</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                disabled={loading}
                minLength={3}
                required
                data-testid="profile-fullname-input"
                style={{ 
                  padding: '0.75rem', 
                  border: '2px solid #e2e8f0', 
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              />
              <small style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                {t('profile.minChars')}
              </small>
            </div>

            {(user?.role === 'admin' || user?.role === 'editor') && (
              <div className="profile-field">
                <label>{t('profile.role')}</label>
                <div className="profile-field-value">
                  <span className={`user-role-badge ${user?.role}`}>
                    {user?.role}
                  </span>
                </div>
              </div>
            )}

            {/* Country selection (always editable, optional) */}
            <div className="profile-field" data-testid="profile-country-field">
              <label>{t('auth.country')}</label>
              {!user?.country && (
                <div
                  data-testid="profile-country-banner"
                  style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)',
                    border: '1px solid #bae6fd',
                    borderRadius: 8,
                    padding: '0.75rem 1rem',
                    marginBottom: '0.75rem',
                    fontSize: '0.9rem',
                    color: '#0c4a6e',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                    {t('auth.pickCountryBannerTitle')}
                  </div>
                  <div>{t('auth.pickCountryBannerText')}</div>
                </div>
              )}
              <CountrySelector
                value={user?.country || null}
                onChange={async (code) => {
                  try {
                    await axios.put(
                      `${API}/auth/me`,
                      { country: code || '' },
                      { headers: getAuthHeader() }
                    );
                    toast.success(t('toast.profileUpdated'));
                    window.location.reload();
                  } catch (err) {
                    console.error('Country update error:', err);
                    toast.error(err.response?.data?.detail || t('toast.saveError'));
                  }
                }}
                disabled={loading}
              />
              <small style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                {t('auth.countryHint')}
              </small>
            </div>

            {/* Language Selection */}
            <div className="profile-field" style={{ overflow: "visible", position: "relative", zIndex: 1000 }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                🌐 {t('profile.interfaceLanguage')}
              </label>
              <LanguageSwitcher />
            </div>

            {isEditing && (
              <>
                <div className="profile-field">
                  <label>
                    <Lock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                    {t('profile.newPasswordOptional')}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="••••••••"
                    style={{ 
                      padding: '0.75rem', 
                      border: '2px solid #e2e8f0', 
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div className="profile-field">
                  <label>{t('auth.confirmPassword')}</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="••••••••"
                    style={{ 
                      padding: '0.75rem', 
                      border: '2px solid #e2e8f0', 
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </>
            )}
          </div>

          <div className="profile-actions">
            <Button type="submit" disabled={loading} data-testid="profile-save-btn">
              <Save size={18} style={{ marginRight: '0.5rem' }} />
              {loading ? t('profile.saving') : t('common.save')}
            </Button>
          </div>
        </form>
        )}
      </div>

      {/* Notifications Section - only for own profile */}
      {isOwnProfile && notifications.length > 0 && (
        <div className="profile-card" style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <span style={{ fontSize: '1.25rem' }}>🔔</span>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{t('profile.notifications')}</h2>
            {notifications.length > 0 && (
              <button
                onClick={clearAllNotifications}
                style={{
                  padding: '4px 8px',
                  fontSize: '0.75rem',
                  background: '#fee2e2',
                  color: '#dc2626',
                  border: '1px solid #fca5a5',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '8px'
                }}
              >
                {t('profile.clearAllNotifications')}
              </button>
            )}
            {notifications.filter(n => !n.read).length > 0 && (
              <span style={{
                background: '#ef4444',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                {notifications.filter(n => !n.read).length}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {notifications.map((notification, index) => (
              <div 
                key={index}
                style={{
                  padding: '1rem',
                  background: notification.read ? '#f9fafb' : (notification.type === 'game_became_free' ? '#ecfdf5' : '#fef3c7'),
                  borderRadius: '8px',
                  border: notification.read ? '1px solid #e5e7eb' : (notification.type === 'game_became_free' ? '2px solid #10b981' : '2px solid #f59e0b')
                }}
              >
                {notification.type === 'game_cancelled_refund' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                      <strong style={{ color: '#92400e' }}>{t('profile.gameCancelled')}</strong>
                    </div>
                    <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem', lineHeight: '1.5' }}>
                      {t('profile.gameCancelledMessage').replace('{game}', notification.game_name_translations?.[language] || notification.game_name_translations?.['ru'] || notification.game_name).replace('{points}', notification.points_refunded)}
                    </p>
                    <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        {new Date(notification.created_at).toLocaleString(language === 'ru' ? 'ru-RU' : 'en-US')}
                      </span>
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        style={{
                          padding: '2px 6px',
                          fontSize: '0.7rem',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fca5a5',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        title={t('profile.deleteNotification')}
                      >
                        ✕
                      </button>
                    </div>
                  </>
                )}
                {notification.type === 'game_became_free' && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.2rem' }}>🎮</span>
                      <strong style={{ color: '#047857' }}>{t('profile.gameFree')}</strong>
                    </div>
                    <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem', lineHeight: '1.5' }}>
                      {t('profile.gameCancelledMessage').replace('{game}', notification.game_name_translations?.[language] || notification.game_name_translations?.['ru'] || notification.game_name).replace('{points}', notification.points_refunded)}
                    </p>
                    <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        {new Date(notification.created_at).toLocaleString(language === 'ru' ? 'ru-RU' : 'en-US')}
                      </span>
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        style={{
                          padding: '2px 6px',
                          fontSize: '0.7rem',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fca5a5',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        title={t('profile.deleteNotification')}
                      >
                        ✕
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Games Section */}
      <div className="profile-card" style={{ marginTop: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <Gamepad2 size={24} style={{ color: '#667eea' }} />
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{isOwnProfile ? t('profile.myGames') : t('profile.games')}</h2>
        </div>
        
        {loadingStats ? (
          <p style={{ color: '#6b7280' }}>{t('profile.loading')}</p>
        ) : userStats?.games?.length > 0 ? (
          <>
            {/* Summary Stats — two-row layout */}
            <div style={{ marginBottom: '1.5rem' }}>
              {/* Row 1 — game-type counters (small) */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '0.75rem',
                marginBottom: '1rem',
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: '0.85rem 1rem',
                  background: '#f8f9fa',
                  borderRadius: '10px',
                }}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#667eea', lineHeight: 1 }}>
                    {userStats.games.filter(g => g.game_mode !== 'objects_only').length}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.4rem' }}>
                    {t('userProfileUI.placesObjects')}
                  </div>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: '0.85rem 1rem',
                  background: '#f8f9fa',
                  borderRadius: '10px',
                }}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#f59e0b', lineHeight: 1 }}>
                    {userStats.games.filter(g => g.game_mode === 'objects_only').length}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.4rem' }}>
                    {t('userProfileUI.objectsOnly')}
                  </div>
                </div>
              </div>

              {/* Row 2 — medal cards (big, eye-catching) - responsive */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: '0.75rem',
              }}>
                {[
                  { p: 1, label: 'Первых мест', count: userStats.first_places || 0,
                    accent: '#FFD700', bg: 'linear-gradient(135deg, #fff8d6 0%, #fff 70%)', border: '#f5d76e' },
                  { p: 2, label: 'Вторых мест', count: userStats.second_places || 0,
                    accent: '#9aa1a8', bg: 'linear-gradient(135deg, #f1f3f5 0%, #fff 70%)', border: '#cbd1d6' },
                  { p: 3, label: 'Третьих мест', count: userStats.third_places || 0,
                    accent: '#CD7F32', bg: 'linear-gradient(135deg, #fbe7d4 0%, #fff 70%)', border: '#e6b785' },
                ].map(({ p, label, count, accent, bg, border }) => (
                  <div
                    key={p}
                    data-testid={`profile-stat-place-${p}`}
                    className="medal-stat-card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      padding: '0.85rem 0.9rem',
                      background: bg,
                      border: `1px solid ${border}`,
                      borderRadius: '12px',
                      minHeight: '92px',
                      flexWrap: 'wrap',
                      justifyContent: 'flex-start',
                      transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <PlaceMedal place={p} height={count > 0 ? 76 : 60}
                      style={count === 0 ? { filter: 'grayscale(85%) opacity(0.55)' } : undefined} />
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '2.1rem',
                        fontWeight: 800,
                        color: count > 0 ? accent : '#cbd1d6',
                        lineHeight: 1,
                      }}>
                        {count}
                      </div>
                      <div style={{
                        fontSize: '0.78rem',
                        color: '#6b7280',
                        marginTop: '0.4rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Regular Games (not objects_only) */}
            {userStats.games.filter(g => g.game_mode !== 'objects_only').length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#374151' }}>
                  {t('userProfileUI.coordinateGames')}
                </h3>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '1rem'
                }}>
                  {userStats.games.filter(g => g.game_mode !== 'objects_only').map(game => (
                    <div 
                      key={game.game_id}
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      onClick={() => navigate(`/games/${game.game_id}/user-results/${user?.id || paramUserId}`)}
                    >
                      <div style={{
                        height: '140px',
                        background: game.has_cover
                          ? `linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.7) 100%), url(${API}/games/${game.game_id}/cover) center/cover`
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        padding: '1rem',
                        position: 'relative'
                      }}>
                        <h4 style={{ 
                          margin: 0, 
                          color: 'white', 
                          fontSize: '1rem',
                          fontWeight: 600,
                          textShadow: '0 1px 3px rgba(0,0,0,0.5)'
                        }}>
                          {game.game_name_translations?.[language] || game.game_name}
                        </h4>
                        {/* Real winner of the game (when player is not the 1st place themselves) */}
                        {game.top_winner_name && (
                          <div
                            data-testid={`profile-game-top-winner-${game.game_id}`}
                            style={{
                              marginTop: '0.35rem',
                              fontSize: '0.72rem',
                              color: 'rgba(255,255,255,0.92)',
                              textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: '0.35rem',
                              lineHeight: 1.25,
                            }}
                          >
                            <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>🏆</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <strong style={{ fontWeight: 600 }}>{game.top_winner_name}</strong>
                              {game.top_winner_at && (
                                <span style={{ opacity: 0.85 }}>
                                  {' · '}
                                  {new Date(game.top_winner_at).toLocaleString('ru-RU', {
                                    day: '2-digit', month: '2-digit', year: '2-digit',
                                    hour: '2-digit', minute: '2-digit',
                                  })}
                                </span>
                              )}
                            </span>
                            {game.score_advantage > 0 && (
                              <span
                                title={`Ваш результат лучше победителя на ${game.score_advantage} очков`}
                                style={{
                                  background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 60%, #fbbf24 100%)',
                                  color: '#fff',
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  padding: '1px 6px',
                                  borderRadius: '999px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.4)',
                                  whiteSpace: 'nowrap',
                                  textShadow: 'none',
                                }}
                              >
                                +{game.score_advantage} оч.
                              </span>
                            )}
                          </div>
                        )}
                        {/* Badges row above title */}
                        <div style={{
                          position: 'absolute',
                          top: '0.5rem',
                          left: '0.5rem',
                          right: '0.5rem',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.35rem',
                          alignItems: 'center'
                        }}>
                          {/* Accuracy with label */}
                          {game.panoramas_in_game > 0 && (
                            <div style={{
                              padding: '0.2rem 0.5rem',
                              backgroundColor: 'rgba(255,255,255,0.95)',
                              borderRadius: '6px',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: '#667eea',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <span style={{ opacity: 0.7 }}>{t('userProfileUI.accuracy')}</span>
                              {((game.total_score / (game.panoramas_in_game * 5000)) * 100).toFixed(0)}%
                            </div>
                          )}
                          {/* Points with label */}
                          <div style={{
                            padding: '0.2rem 0.5rem',
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: '#667eea',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            <span style={{ opacity: 0.7 }}>{t('userProfileUI.glasses')}</span>
                            {game.total_score}
                          </div>
                          {/* Coordinates - icon only */}
                          <div style={{
                            padding: '0.2rem 0.5rem',
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: game.panoramas_answered >= game.panoramas_in_game ? '#10b981' : '#ef4444'
                          }}>
                            📍 {game.panoramas_answered || 0}/{game.panoramas_in_game || '?'}
                          </div>
                          {/* Objects - icon only */}
                          {game.total_hotspots > 0 && (
                            <div style={{
                              padding: '0.2rem 0.5rem',
                              backgroundColor: 'rgba(255,255,255,0.95)',
                              borderRadius: '6px',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: (game.found_hotspots || 0) >= game.total_hotspots ? '#10b981' : '#f59e0b'
                            }}>
                              🎯 {game.found_hotspots || 0}/{game.total_hotspots}
                            </div>
                          )}
                          {/* Winner medal + best-answer overlay */}
                          {(game.winner_place || game.best_answer) && (
                            <div style={{ fontSize: '1.1rem' }}>
                              <PlaceMedal
                                place={game.winner_place}
                                bestAnswer={!!game.best_answer}
                                size="sm"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Objects Only Games */}
            {userStats.games.filter(g => g.game_mode === 'objects_only').length > 0 && (
              <div>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#374151' }}>
                  {t('userProfileUI.objectSearchGames')}
                </h3>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '1rem'
                }}>
                  {userStats.games.filter(g => g.game_mode === 'objects_only').map(game => (
                    <div 
                      key={game.game_id}
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      onClick={() => navigate(`/games/${game.game_id}/user-results/${user?.id || paramUserId}`)}
                    >
                      <div style={{
                        height: '140px',
                        background: game.has_cover
                          ? `linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.7) 100%), url(${API}/games/${game.game_id}/cover) center/cover`
                          : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        padding: '1rem',
                        position: 'relative'
                      }}>
                        <h4 style={{ 
                          margin: 0, 
                          color: 'white', 
                          fontSize: '1rem',
                          fontWeight: 600,
                          textShadow: '0 1px 3px rgba(0,0,0,0.5)'
                        }}>
                          {game.game_name_translations?.[language] || game.game_name}
                        </h4>
                        {/* Badges row above title */}
                        <div style={{
                          position: 'absolute',
                          top: '0.5rem',
                          left: '0.5rem',
                          right: '0.5rem',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.35rem',
                          alignItems: 'center'
                        }}>
                          {/* Points with label */}
                          <div style={{
                            padding: '0.2rem 0.5rem',
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: '#f59e0b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            <span style={{ opacity: 0.7 }}>очки</span>
                            {game.total_score}
                          </div>
                          {/* Objects - icon only */}
                          <div style={{
                            padding: '0.2rem 0.5rem',
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: (game.found_hotspots || 0) >= game.total_hotspots ? '#10b981' : '#f59e0b'
                          }}>
                            🎯 {game.found_hotspots || 0}/{game.total_hotspots || '?'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy games list support - hidden */}
            <div style={{ display: 'none' }}>
              {userStats.games.map(game => (
                <div 
                  key={game.game_id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }}
                >
                  {/* Full card with cover as background */}
                  <div
                    onClick={() => navigate(`/games/${game.game_id}/user-results/${user?.id || paramUserId}`)}
                    style={{
                      minHeight: '200px',
                      background: game.has_cover 
                        ? `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%), url(${API}/games/${game.game_id}/cover) center/cover`
                        : 'linear-gradient(135deg, #667eea, #764ba2)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    {/* Top row: Game name only */}
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '1rem',
                        backdropFilter: 'blur(4px)',
                        maxWidth: '90%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {game.game_name_translations && game.game_name_translations[language] ? game.game_name_translations[language] : game.game_name}
                      </div>
                    </div>
                    
                    {/* Bottom section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {/* Stats row: Score, Accuracy, Panoramas, Hotspots */}
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {/* Score */}
                        <div style={{
                          textAlign: 'center',
                          padding: '0.4rem 0.6rem',
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          borderRadius: '8px',
                          minWidth: '60px'
                        }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#667eea' }}>
                            {game.total_score?.toLocaleString('ru-RU') || 0}
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>{t('profile.points')}</div>
                        </div>
                        
                        {/* Accuracy */}
                        <div style={{
                          textAlign: 'center',
                          padding: '0.4rem 0.6rem',
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          borderRadius: '8px',
                          minWidth: '50px'
                        }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700, color: game.winner_place ? '#d97706' : '#667eea' }}>
                            {game.avg_accuracy}%
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>{t('profile.accuracy')}</div>
                        </div>
                        
                        {/* Panoramas guessed */}
                        <div 
                          title={t('profile.guessedPlacesTooltip') || 'Угаданные места'}
                          style={{
                            textAlign: 'center',
                            padding: '0.4rem 0.6rem',
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            borderRadius: '8px',
                            minWidth: '50px',
                            cursor: 'help'
                          }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700, color: game.panoramas_answered >= game.panoramas_in_game ? '#10b981' : '#ef4444' }}>
                            {game.panoramas_answered || 0}/{game.panoramas_in_game || '?'}
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>📍</div>
                        </div>
                        
                        {/* Hotspots found - only show if there are hotspots */}
                        {game.total_hotspots > 0 && (
                          <div 
                            title={t('profile.foundObjectsTooltip') || 'Найденные объекты'}
                            style={{
                              textAlign: 'center',
                              padding: '0.4rem 0.6rem',
                              backgroundColor: 'rgba(255,255,255,0.95)',
                              borderRadius: '8px',
                              minWidth: '50px',
                              cursor: 'help'
                            }}>
                            <div style={{ fontSize: '1rem', fontWeight: 700, color: (game.found_hotspots || 0) >= game.total_hotspots ? '#10b981' : '#f59e0b' }}>
                              {game.found_hotspots || 0}/{game.total_hotspots}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>🎯</div>
                          </div>
                        )}
                      </div>
                      
                      {/* Bottom row: Share buttons + Medal */}
                      <div 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '6px'
                        }}
                      >
                        <ShareButtons 
                          url={getGameResultUrl(game.game_id)}
                          text={getGameShareText(game.game_name_translations?.[language] || game.game_name_translations?.['ru'] || game.game_name, game.avg_accuracy)}
                          compact={true}
                          whiteShadow={true}
                          shareToTelegram={shareToTelegram}
                          shareToWhatsApp={shareToWhatsApp}
                          shareToVK={shareToVK}
                          shareToOK={shareToOK}
                          copyToClipboard={copyToClipboard}
                        />
                        
                        {/* Winner medal + best-answer overlay (inline with share buttons) */}
                        {(game.winner_place || game.best_answer) && (
                          <div style={{
                            fontSize: '2rem',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                          }}>
                            <PlaceMedal
                              place={game.winner_place}
                              bestAnswer={!!game.best_answer}
                              size="md"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem',
            color: '#6b7280'
          }}>
            <Gamepad2 size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>{t('profile.noGames')}</p>
            <Button 
              onClick={() => navigate('/')}
              style={{ marginTop: '1rem' }}
            >
              {t('profile.startPlaying')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
